import React, { useState, useMemo, useEffect } from "react";

import _ from "lodash";
import styled from "styled-components";

import Icon from "shared/components/Icon";
import { ActionLink } from "shared/components/Links";
import Well from "shared/components/Well";
import { useVeroFlag } from "shared/utils/featureFlags";
import { VeroFlag } from "shared/utils/veroFlags";
import { deriveOutcomeColor, displayName, selectOutcomeBands } from "./utils";

function getOutcomeIcon(outcome) {
  const color = deriveOutcomeColor(outcome);
  switch (color) {
    case "fail":
      return Icon.CrossRedSmall;
    case "warn":
      return Icon.OrangeExclamation;
    case "pass":
    default:
      return Icon.CheckGreenSmall;
  }
}

// Helper to get border color *value* based on severity
function getBorderColorValue(outcome) {
  const color = deriveOutcomeColor(outcome);
  switch (color) {
    case "fail":
      // Attempt to use CSS variable, fallback to hex
      return "var(--vega-color-critical, #d9534f)";
    case "warn":
      return "var(--vega-color-warning, #f0ad4e)";
    case "pass":
    default:
      return "var(--vega-color-success, #5cb85c)";
  }
}

// Subtle background tint per severity
function getBackgroundColorValue(outcome, isExpanded = false) {
  const opacity = isExpanded ? 0.05 : 0.02; // Even subtler when collapsed
  const color = deriveOutcomeColor(outcome);
  switch (color) {
    case "fail":
      return `rgba(217, 83, 79, ${opacity})`; // light red
    case "warn":
      return `rgba(240, 173, 78, ${opacity})`; // light orange
    case "pass":
    default:
      return `rgba(92, 184, 92, ${opacity})`; // light green
  }
}

// Helper function to check for valid recommendations
const hasValidRecommendations = (recommendations) =>
  // Check if the array exists, is not empty, and has at least one non-empty string
  !_.isEmpty(recommendations) &&
  _.some(recommendations, (r) => _.isString(r) && r.trim() !== "");

const LG_TONES = {
  warn: { border: "#F0A05A", bg: "#FFF8F0", circle: "#FFE4CC", glyph: "#8A4B00", label: "#8A5A00", link: "#B35500" },
  fail: { border: "#F0728C", bg: "#FFF4F6", circle: "#FAE4E9", glyph: "#8E1B33", label: "#8E1B33", link: "#B3244A" },
  pass: { border: "#05CE88", bg: "#F2FBF7", circle: "#D8F3EA", glyph: "#03684A", label: "#03684A", link: "#03684A" },
};

const LgList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
`;

const LgRow = styled.div<{ $tone: keyof typeof LG_TONES }>`
  border: 1px solid ${({ $tone }) => LG_TONES[$tone].border};
  border-radius: 6px;
  padding: 10px 13px;
  background: ${({ $tone }) => LG_TONES[$tone].bg};
  display: flex;
  flex-direction: column;
  gap: 9px;
`;

const LgHead = styled.div`
  display: flex;
  align-items: center;
  gap: 11px;
`;

const LgCircle = styled.span<{ $tone: keyof typeof LG_TONES }>`
  width: 17px;
  height: 17px;
  border-radius: 50%;
  background: ${({ $tone }) => LG_TONES[$tone].circle};
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  font: 600 11px "Work Sans", sans-serif;
  color: ${({ $tone }) => LG_TONES[$tone].glyph};
`;

const LgLabel = styled.span<{ $tone: keyof typeof LG_TONES }>`
  font: 500 11.5px "Work Sans", sans-serif;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${({ $tone }) => LG_TONES[$tone].label};
`;

const LgLink = styled.button<{ $tone: keyof typeof LG_TONES }>`
  font: 500 11.5px "Work Sans", sans-serif;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${({ $tone }) => LG_TONES[$tone].link};
  background: none;
  border: 0;
  padding: 0;
  cursor: pointer;
  white-space: nowrap;
`;

const LgBody = styled.p`
  font: 300 12.5px/1.45 "Work Sans", sans-serif;
  color: #5b5670;
  margin: 0 0 0 28px;
`;

const LgGlyph = ({ tone }: { tone: keyof typeof LG_TONES }) => {
  if (tone === "warn") return <>!</>;
  const stroke = LG_TONES[tone].glyph;
  return (
    <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke={stroke} strokeWidth="3.4" strokeLinecap="round" strokeLinejoin="round">
      {tone === "fail" ? <path d="M18 6L6 18M6 6l12 12" /> : <path d="M20 6L9 17l-5-5" />}
    </svg>
  );
};

const OutcomesV2 = ({ lumenRun, lgTone = false }) => {
  const useDealsListUiRedesign = useVeroFlag(VeroFlag.useDealsListUiRedesign);

  // Generate a stable key for each item based on its score label
  const getItemKey = (item) => item.outcome?.scoreLabel;

  // Memoize all computed values to ensure hooks are called consistently
  const { sortedOutcomesWithEntries, itemsWithRecs } = useMemo(() => {
    const sorted = selectOutcomeBands(lumenRun).map((outcome) => ({ outcome }));
    const withRecs = _.filter(sorted, (item) =>
      hasValidRecommendations(item.outcome.recommendations)
    );
    return { sortedOutcomesWithEntries: sorted, itemsWithRecs: withRecs };
  }, [lumenRun]);

  // State for Accordion Items
  // NOTE: All hooks must be called before any early returns
  const [expandedKeys, setExpandedKeys] = useState({});

  // Initialize expanded state based on feature flag (only once when data is available)
  useEffect(() => {
    // Legacy behavior: expand first item with recommendations
    if (!useDealsListUiRedesign && !lgTone && itemsWithRecs.length > 0) {
      const firstItemKey = getItemKey(itemsWithRecs[0]);
      if (firstItemKey) {
        setExpandedKeys((prev) => {
          // Only set if not already expanded (avoid unnecessary updates)
          if (!prev[firstItemKey]) {
            return { [firstItemKey]: true };
          }
          return prev;
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [useDealsListUiRedesign, itemsWithRecs.length]);

  const toggleExpand = (key) => {
    setExpandedKeys((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  // Early return AFTER all hooks have been called
  if (_.isEmpty(sortedOutcomesWithEntries)) {
    return null;
  }

  if (lgTone) {
    return (
      <LgList>
        {_.map(sortedOutcomesWithEntries, (item) => {
          const itemKey = getItemKey(item);
          const tone = deriveOutcomeColor(item.outcome);
          const recs = _.filter(
            item.outcome.recommendations,
            (r) => _.isString(r) && r.trim() !== ""
          );
          const isExpanded = recs.length > 0 && !!expandedKeys[itemKey];
          return (
            <LgRow key={itemKey} $tone={tone}>
              <LgHead>
                <LgCircle $tone={tone}>
                  <LgGlyph tone={tone} />
                </LgCircle>
                <LgLabel $tone={tone}>
                  {displayName(item.outcome.scoreLabel) || item.outcome.scoreLabel}
                </LgLabel>
                {recs.length > 0 && (
                  <LgLink type="button" $tone={tone} onClick={() => toggleExpand(itemKey)}>
                    {isExpanded ? "Hide details" : "Details"}
                  </LgLink>
                )}
              </LgHead>
              {isExpanded && recs.map((r) => <LgBody key={r}>{r}</LgBody>)}
            </LgRow>
          );
        })}
      </LgList>
    );
  }

  return (
    <div
      className="vega content flex column gap"
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "1rem",
        width: "100%",
        alignItems: "stretch",
      }}
    >
      {/* Render all items uniformly */}
      {_.map(sortedOutcomesWithEntries, (item) => {
        const itemKey = getItemKey(item);
        const hasRecs = hasValidRecommendations(item.outcome.recommendations);
        // Only items with recommendations can be expanded/collapsed
        const isExpanded = hasRecs ? !!expandedKeys[itemKey] : false;
        const OutcomeIcon = getOutcomeIcon(item.outcome);
        const title =
          displayName(item.outcome.scoreLabel) || item.outcome.scoreLabel;
        const borderTopColorValue = getBorderColorValue(item.outcome);
        // const showDescription = !!item.entry?.explanation?.output?.description;

        return (
          <div
            key={itemKey}
            className={`vega card outline padding ${
              isExpanded ? "medium" : "small"
            } flex column gap`}
            onClick={hasRecs ? () => toggleExpand(itemKey) : undefined}
            onKeyPress={
              hasRecs
                ? (e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      toggleExpand(itemKey);
                    }
                  }
                : undefined
            }
            role={hasRecs ? "button" : undefined}
            tabIndex={hasRecs ? 0 : undefined}
            style={{
              display: "flex",
              flexDirection: "column",
              width: "100%",
              maxWidth: "100%",
              boxShadow: isExpanded
                ? "0 2px 4px rgba(0, 0, 0, 0.05), 0 4px 8px rgba(0, 0, 0, 0.05)"
                : "0 1px 2px rgba(0, 0, 0, 0.03), 0 2px 4px rgba(0, 0, 0, 0.03)",
              borderRadius: "0.5rem",
              borderTopWidth: "2px",
              borderTopStyle: "solid",
              borderTopColor: borderTopColorValue,
              backgroundColor: getBackgroundColorValue(
                item.outcome,
                isExpanded
              ),
              cursor: hasRecs ? "pointer" : "default",
              transition: "all 0.2s ease",
            }}
          >
            {/* Single row header with title and details link */}
            <div className="vega flex no-wrap gap items-center justify-between">
              <div className="vega flex no-wrap gap items-center">
                <OutcomeIcon
                  className={`vega icon ${isExpanded ? "large" : "medium"}`}
                />
                <span
                  className="vega title small"
                  style={{
                    alignSelf: "center",
                    fontSize: isExpanded ? undefined : "0.9em",
                  }}
                >
                  {title}
                </span>
              </div>
              {/* Details link in header when collapsed */}
              {hasRecs && !isExpanded && (
                <ActionLink
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleExpand(itemKey);
                  }}
                  style={{
                    color: getBorderColorValue(item.outcome),
                    fontSize: "0.85em",
                    whiteSpace: "nowrap",
                  }}
                >
                  Details
                </ActionLink>
              )}
            </div>

            {/* Expanded content area */}
            {hasRecs && isExpanded && (
              <div
                className="vega flex column gap-small"
                style={{ marginTop: "0.75rem" }}
              >
                <div className="vega flex column gap-small">
                  {_.map(
                    _.filter(
                      item.outcome.recommendations,
                      (r) => _.isString(r) && r.trim() !== ""
                    ),
                    (r, index) => (
                      <Well key={index} className="vega info subtle-bg">
                        {r}
                      </Well>
                    )
                  )}
                </div>

                {/* Hide Details link at bottom when expanded */}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "flex-end",
                    marginTop: "0.5rem",
                  }}
                >
                  <ActionLink
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleExpand(itemKey);
                    }}
                    style={{
                      color: getBorderColorValue(item.outcome),
                      fontSize: "0.85em",
                    }}
                  >
                    Hide Details
                  </ActionLink>
                </div>
              </div>
            )}
          </div>
        );
      })}
      {/* Removed the separate container for remaining items */}
    </div>
  );
};

export default OutcomesV2;
